import { useCallback, useState } from 'react';
import { useLayout } from 'shared/state/layoutContext';
import { useRackView } from './useRackView';

/** Racks drawn side by side before the section pages. Three is what fits the
 *  workbench column at the 1280px laptop floor next to the rail. */
export const RACKS_PER_PAGE = 3;

/** The inline, always-visible rack readout. Everything about WHAT is in the
 *  racks comes from useRackView — the same hook the Rack View overlay runs —
 *  so the two can never draw different layouts; this only adds paging and the
 *  jump into the overlay. `perPage` is how many racks are on show at once: three
 *  in the workbench section, one in the rail's compact rack. */
export function useRackSection(perPage: number = RACKS_PER_PAGE) {
  const r = useRackView();
  const layout = useLayout();
  const [requestedPage, setRequestedPage] = useState(0);

  const total = r.racks.length;
  const lastPage = Math.max(0, Math.ceil(total / perPage) - 1);
  // Clamped on read, not in an effect: the rack count follows the configuration,
  // and a page that no longer exists must not flash an empty section first.
  const page = Math.min(requestedPage, lastPage);
  const start = page * perPage;
  const visible = r.racks.slice(start, start + perPage);

  const framed = r.rackSize !== '0';
  const openRackView = useCallback(() => layout.openOverlay('rackView'), [layout]);

  return {
    visible,
    total,
    paged: total > perPage,
    rangeLabel: `Racks ${start + 1}–${start + visible.length} of ${total}`,
    canPrev: page > 0,
    canNext: page < lastPage,
    onPrev: () => setRequestedPage(Math.max(0, page - 1)),
    onNext: () => setRequestedPage(Math.min(lastPage, page + 1)),
    framed,
    summary: `${total} rack${total === 1 ? '' : 's'} · ${framed ? `${r.rackSize}U` : 'no frame'}`,
    rackSize: r.rackSize,
    rackSizeOptions: r.rackSizeOptions,
    onRackSizeChange: r.onRackSizeChange,
    vendorId: r.vendorId,
    isPhotographic: r.isPhotographic,
    openRackView,
  };
}
